import { useEffect, useMemo, useRef, useState } from "react";
import cabezaUrl from "../assets/robot-head.png";
import { logoDex } from "./Monedas";

/**
 * El robot-mascota recorriendo TODA la portada tipo Pac-Man: baja en zigzag (serpentina) comiéndose
 * los "pellets" (los puntitos del fondo) y, cuando pasa por encima, los logos de las DEX que
 * soportamos. Es un guiño, va DETRÁS del contenido (z bajo, pointer-events none) para no estorbar, y
 * se apaga entero con `prefers-reduced-motion`.
 *
 * El bicho se mueve por transform en un `requestAnimationFrame`; los pellets/logos se marcan como
 * "comido" tocando el DOM directamente (nada de re-render por frame). Al terminar la ronda, reaparecen.
 */

const DEXS = ["UNISWAP", "ROBINHOOD", "PRJX", "PANCAKESWAP"] as const;
const MX = 46;          // margen lateral
const GAP_LANE = 168;   // separación entre carriles
const GAP_PELLET = 52;  // separación entre pellets
const SPEED = 175;      // px/s

type Pt = { x: number; y: number };

function construir(W: number, H: number) {
  const carriles: number[] = [];
  for (let y = 70; y <= H - 70; y += GAP_LANE) carriles.push(y);
  if (carriles.length < 2) carriles.push(H - 70);

  // pellets por carril
  const pellets: Pt[] = [];
  carriles.forEach((y) => {
    for (let x = MX; x <= W - MX; x += GAP_PELLET) pellets.push({ x, y });
  });

  // waypoints en serpentina (izq→der, der→izq…)
  const pts: Pt[] = [];
  carriles.forEach((y, i) => {
    if (i % 2 === 0) { pts.push({ x: MX, y }); pts.push({ x: W - MX, y }); }
    else { pts.push({ x: W - MX, y }); pts.push({ x: MX, y }); }
  });

  // longitudes acumuladas
  const seg = [];
  let len = 0;
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i]!, b = pts[i + 1]!;
    const d = Math.hypot(b.x - a.x, b.y - a.y);
    const dir = b.x > a.x ? 1 : b.x < a.x ? -1 : 0;
    seg.push({ a, b, d, ini: len, dir });
    len += d;
  }

  // 4 logos de DEX repartidos en carriles distintos
  const dex = DEXS.map((marca, k) => {
    const ci = Math.min(carriles.length - 1, [0, 2, 4, 6][k]! % Math.max(1, carriles.length));
    const frac = [0.32, 0.68, 0.24, 0.76][k]!;
    return { marca, x: MX + frac * (W - 2 * MX), y: carriles[ci]! };
  });

  return { pellets, dex, seg, len };
}

function puntoEn(seg: ReturnType<typeof construir>["seg"], d: number) {
  let dir = 1;
  for (const s of seg) {
    if (d <= s.ini + s.d) {
      const f = s.d ? (d - s.ini) / s.d : 0;
      if (s.dir) dir = s.dir;
      return { x: s.a.x + (s.b.x - s.a.x) * f, y: s.a.y + (s.b.y - s.a.y) * f, dir };
    }
    if (s.dir) dir = s.dir;
  }
  const last = seg[seg.length - 1];
  return { x: last?.b.x ?? 0, y: last?.b.y ?? 0, dir };
}

export function PacmanBot() {
  const cont = useRef<HTMLDivElement>(null);
  const bot = useRef<HTMLDivElement>(null);
  const [layout, setLayout] = useState<ReturnType<typeof construir> | null>(null);

  useEffect(() => {
    const el = cont.current;
    if (!el) return;
    let tmr = 0;
    const build = () => {
      window.clearTimeout(tmr);
      tmr = window.setTimeout(() => {
        const W = el.clientWidth, H = el.clientHeight;
        if (W > 0 && H > 0) setLayout(construir(W, H));
      }, 150);
    };
    build();
    const ro = new ResizeObserver(build);
    ro.observe(el);
    return () => { ro.disconnect(); window.clearTimeout(tmr); };
  }, []);

  useEffect(() => {
    if (!layout || !cont.current || !bot.current) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    const nodos = Array.from(cont.current.querySelectorAll<HTMLElement>(".pac-com"));
    nodos.forEach((n) => n.classList.remove("comido"));
    const dur = (layout.len / SPEED) * 1000;
    let t0: number | null = null, prev = 0, raf = 0;
    const loop = (ts: number) => {
      if (t0 === null) t0 = ts;
      const d = (((ts - t0) / dur) * layout.len) % layout.len;
      if (d < prev) nodos.forEach((n) => n.classList.remove("comido"));
      prev = d;
      const p = puntoEn(layout.seg, d);
      bot.current!.style.transform = `translate(${p.x}px, ${p.y}px) translate(-50%, -50%) scaleX(${p.dir || 1})`;
      for (const n of nodos) {
        if (n.classList.contains("comido")) continue;
        const nx = +n.dataset.x!, ny = +n.dataset.y!;
        if (Math.hypot(nx - p.x, ny - p.y) < 24) n.classList.add("comido");
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [layout]);

  const contenido = useMemo(() => {
    if (!layout) return null;
    return (
      <>
        {layout.pellets.map((p, i) => (
          <span key={`p${i}`} className="pac-pellet pac-com" data-x={p.x} data-y={p.y}
            style={{ left: p.x, top: p.y }} />
        ))}
        {layout.dex.map((d) => {
          const url = logoDex(d.marca);
          return (
            <span key={d.marca} className="pac-dex pac-com" data-x={d.x} data-y={d.y}
              style={{ left: d.x, top: d.y }}>
              {url && <img src={url} alt="" draggable={false} />}
            </span>
          );
        })}
      </>
    );
  }, [layout]);

  return (
    <div className="pacman" ref={cont} aria-hidden>
      {contenido}
      <div className="pac-bot" ref={bot}>
        <img src={cabezaUrl} alt="" draggable={false} />
      </div>
    </div>
  );
}
